import React, { useCallback, useEffect, useState } from "react";
import { Select } from "@mantine/core";
import {
  DEFAULT_TEMPLATE_SORT,
  TEMPLATE_SORT_OPTIONS,
  TemplateSort,
} from "./useSortedTemplates";

// Tab-scoped persistence: sessionStorage survives reload, resets when the
// tab is closed. Matches the requested UX ("keep it as long as the tab is open").
const STORAGE_KEY = "formTemplates.sortBy";

function isTemplateSort(value: unknown): value is TemplateSort {
  return (
    typeof value === "string" &&
    TEMPLATE_SORT_OPTIONS.some((opt) => opt.value === value)
  );
}

function readPersistedSort(): TemplateSort {
  if (typeof window === "undefined") return DEFAULT_TEMPLATE_SORT;
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (isTemplateSort(stored)) return stored;
  } catch {
    // sessionStorage can throw in private mode / disabled storage; fall through.
  }
  return DEFAULT_TEMPLATE_SORT;
}

function writePersistedSort(value: TemplateSort): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // No-op: a failure to persist must not break the UI.
  }
}

export function usePersistedTemplateSort(): [
  TemplateSort,
  (next: TemplateSort) => void,
] {
  const [value, setValue] = useState<TemplateSort>(readPersistedSort);
  useEffect(() => {
    writePersistedSort(value);
  }, [value]);
  return [value, setValue];
}

interface TemplateSortControlProps {
  value: TemplateSort;
  onChange: (value: TemplateSort) => void;
  /** Compact variant for the picker modal; default fits the settings page. */
  size?: "xs" | "sm";
  /** Override the visible label; omit for "Sort by". */
  label?: string;
}

export const TemplateSortControl: React.FC<TemplateSortControlProps> = ({
  value,
  onChange,
  size = "sm",
  label = "Sort by",
}) => {
  const handleChange = useCallback(
    (next: string | null) => {
      if (isTemplateSort(next)) onChange(next);
    },
    [onChange],
  );

  return (
    <Select
      label={label}
      size={size}
      value={value}
      onChange={handleChange}
      data={TEMPLATE_SORT_OPTIONS}
      allowDeselect={false}
      checkIconPosition="right"
      w={220}
    />
  );
};
